import type { SyntheticEvent } from "react";
import type { FinanceRateStateType } from "../redux/reducers/FinanceRateReducer";
import { isFinanceCompareQuotesSet, showBnbMonthlyRates, showExternalFrame } from "./settings/bnbFinanceSettingUtils";
import { isCod } from "../../../../common-deprecated/settings/utils/commonSettingUtils";
import { getBnbOptionalLabel } from "./labels";
import { FINANCE_CALCULATOR, INSURANCE_CALCULATOR } from "../../../../common-deprecated/constants";
import type { CommonSettingsType } from "../../../../common-deprecated/settings/fetchCommonSettings";
import type { FinanceOptionType } from "../../../../common-deprecated/types/CommonTypes";
import type { BuildAndBuyDispatchType } from "../redux/store";
import type { BnbSettingsType } from "../redux/reducers/BnbSettingsReducer";
import { setStep } from "../redux/actions/NavigationActions";
import { type FinanceInsuranceCalculatorType, isPSLSDataValidOnFinance } from "./FinanceInsuranceCalculatorLink";
import { scrollIntoViewAndOpen } from "./scrollUtils";
import { trackFinanceToggle, trackNavGoToStep } from "./tracking";
import { type NavOrigin, NavStep } from "../../shared-logic/utils/Navigation";
import { showServicesStep } from "./settings/bnbSettingUtils";
import { setFinanceOption } from "../../../../common-deprecated/redux/actions/UtilActions";
import type { CarType } from "../../../shared-logic/types/CarType";
import type { RetailerSettingsType } from "../../../../shared-logic/features/retailer/redux/RetailerSettingsSlice";

export const goToFinanceInsurance = (
    event: SyntheticEvent,
    payload: {
        bnbSettings: BnbSettingsType;
        retailerSettings: RetailerSettingsType;
        dispatch: BuildAndBuyDispatchType;
        origin: NavOrigin;
        step: NavStep;
        target: FinanceInsuranceCalculatorType;
        quotes?: {
            finance: string | null;
            insurance: string | null;
            enabled: boolean;
        };
        financeOption?: FinanceOptionType;
        targetConfigured?: boolean;
    },
): void => {
    const showServices = showServicesStep(payload.bnbSettings, payload.retailerSettings) && payload.targetConfigured;
    // Change FinanceOption in Sidepanel
    if (
        payload.target === "finance" &&
        payload.financeOption &&
        payload.financeOption !== "monthly" &&
        isFinanceCompareQuotesSet(payload.bnbSettings.enableFinanceCompare, payload.quotes?.finance || null)
    ) {
        payload.dispatch(setFinanceOption("monthly", false));
        trackFinanceToggle(payload.step, "monthly", "cc-action");
    }
    // Go to Nearest Step if not one of the allowed options
    if (
        payload.step !== NavStep.Customize &&
        (payload.step !== NavStep.Services || (!showServices && payload.step === NavStep.Services))
    ) {
        const navigateToServicesIfIncluded = [NavStep.Summary, NavStep.Buy, NavStep.Reserve, NavStep.Lease];
        const destination =
            showServices && navigateToServicesIfIncluded.includes(payload.step) ? NavStep.Services : NavStep.Customize;

        // CHANGE STEP TO NEAREST TARGET
        trackNavGoToStep(payload.step, destination);
        event.preventDefault();
        payload.dispatch(setStep(destination, payload.origin));
    } else {
        scrollIntoViewAndOpen(payload.dispatch, payload.target, true);
    }
};

export const getFinanceInsuranceLabelKey = (params: {
    type: FinanceInsuranceCalculatorType;
    bnbSettings: BnbSettingsType;
    commonSettings: CommonSettingsType;
    quotes: {
        finance: string | null;
        insurance: string | null;
        enabled: boolean;
    };
}): "financeButton" | "financeButtonEdit" | "insuranceButton" | "insuranceButtonEdit" => {
    const { type, bnbSettings, commonSettings, quotes } = params;
    if (type === FINANCE_CALCULATOR) {
        if (quotes && quotes.finance && getBnbOptionalLabel({ commonSettings, bnbSettings }, "financeButtonEdit")) {
            return "financeButtonEdit";
        } else {
            return "financeButton";
        }
    } else if (type === INSURANCE_CALCULATOR) {
        if (quotes && quotes.insurance && getBnbOptionalLabel({ commonSettings, bnbSettings }, "insuranceButtonEdit")) {
            return "insuranceButtonEdit";
        } else {
            return "insuranceButton";
        }
    }

    return "financeButton";
};

export const showFinanceInsurance = (params: {
    type: FinanceInsuranceCalculatorType;
    bnbSettings: BnbSettingsType;
    car: CarType;
    commonSettings: CommonSettingsType;
    finance: FinanceRateStateType;
    step: NavStep;
}): boolean => {
    const { type, bnbSettings, car, commonSettings, finance, step } = params;
    return (
        !isCod(commonSettings) &&
        showExternalFrame(commonSettings, bnbSettings, type, car) &&
        // Finance Compare doesn't need a valid ccGetMonthlyRate response before showing financeInsurance so PSLS check is not required for finance compare
        ((bnbSettings.enableFinanceCompare &&
            type === "finance" &&
            showBnbMonthlyRates(commonSettings, bnbSettings, car.model.internalCode || "", step, car)) ||
            isPSLSDataValidOnFinance(type, finance, bnbSettings))
    );
};

export const hasInsuranceConfigured = (requireDefaultInsurance: boolean, isPersonalizedQuote: boolean): boolean => {
    return !!(requireDefaultInsurance || isPersonalizedQuote);
};
